/* DC Hero (block 1): sizing (step 2) plus motion (step 3) plus the Spline hall layer (lane C).
   Sizing writes the 2.5D world scale to the section root per HERO-STAGE-SPEC section 2:
     --dc-header  height of the store's header group (px), the stage is 100svh minus it
     --dc-s       H / 900 (1 u = 1 px on a 900 px stage)
     --dc-P       1.2071 * H (CSS perspective for a 45 degree vertical FOV)
     --dc-camz    landing camera z: 1500 on desktop, 2600 under 768 px
   Motion (needs window.__dcEvent from dc-event.js, otherwise the landing stays static):
     a. load-in beat, time based, once per page: lamps flare, banner clip-path wipe,
        pillar copy up, ceiling fixtures near to far, floor reflection follows the lamps
     b. pinned scrub: ScrollTrigger pins the stage for the section's scroll length,
        scrub 0.6; camZ = camZ0 - (camZ0 + 1500) * n drives the scene translateZ through
        a gsap quickSetter; any plane whose distance (camZ - z) drops under 80 u fades to
        opacity 0 and visibility hidden (and back on the way up). The stage also grows
        from 100svh minus header to the full viewport over n 0 to 0.15 (the header hides
        on scroll down, the walk fills the frame); the section carries that extra height
        as padding so the stage never overlaps block 2 after the pin.
     c. panel 1 (plus SCROLL hint and index) live under n 0.22, panel 2 live 0.55 to 0.9
     d. hand-off darkening 0.85 to 1
     e. reduced motion or Save-Data: no pin, no scrub, no beat, static landing
   Spline hall (lane C, HERO-STAGE-SPEC section 2 lane B mapping and section 6 lane C):
     f. capability gate. Desktop path when 1024 px and up, pointer fine, no Save-Data, no
        reduced motion, deviceMemory undefined or 4 and up, hardwareConcurrency undefined
        or 4 and up, and the section's hall_on_desktop is on. Every other viewport gets
        the opt-in button when hall_opt_in_mobile is on and the memory and cores test
        passes (hidden under Save-Data or reduced motion). No scene URL, no hall.
     g. loader. Dynamic import of the self-hosted runtime, new Application on a canvas
        created inside .dc-hero__hall (between the DOM planes and the copy layer),
        app.load(scene), 8 s guard: failure or timeout disposes and keeps the DOM
        (console.debug once). Success: camX 0 and camY 40 once, camZ on every scrub tick
        (coalesced to one setVariable per animation frame), is-hall on the root, the DOM
        world cross-fades out over 0.6 s (CSS) and stays in the DOM with visibility hidden.
        Copy layer, corner metadata and the rail stay on top. The app stops while the
        stage is not intersecting and plays again when it is.
     h. teardown on shopify:section:unload, on destroy, on reduced motion or Save-Data,
        and on every breakpoint change (768 motion rebuild, 1024 hall gate, pointer type),
        after which the gate is evaluated again. On phones the opt-in button becomes the
        close control while the hall is loaded.
   gsap.matchMedia tears down and rebuilds across the 768 px breakpoint and on a
   reduced-motion change. Theme editor: shopify:section:load re-inits (existing triggers
   for that root are killed first), shopify:section:unload tears down; roots that left
   the document without an unload are swept on the next initAll or their next resize.
   root.__dcHero = { recompute, setProgress(n), getCamZ(), triggers, progress, armed,
   motion, app, hallState, hall, hallLoad(mode), hallClose(), destroy }. */
(() => {
  if (window.__dcHeroBoot) return;
  window.__dcHeroBoot = true;

  const MOBILE = '(max-width: 767px)';
  const DESKTOP = '(min-width: 768px)';
  const REDUCE = '(prefers-reduced-motion: reduce)';
  const HALL_DESKTOP = '(min-width: 1024px)';
  const HALL_FINE = '(pointer: fine)';
  const HALL_TIMEOUT = 8000;  // ms from load start to giving up on the hall
  const HALL_MIN_MEMORY = 4;  // GB, navigator.deviceMemory when reported
  const HALL_MIN_CORES = 4;   // navigator.hardwareConcurrency when reported
  const CAM_END = -1500;
  const EYE_HIDE = 80;        // spec: under 80 u from the eye a plane is hidden
  const EYE_BAND = 120;       // it fades over the 120 u before that
  /* A plane is also hidden once its projected size would pass this many CSS px on its
     larger side (dim_u * P / dist). A 3D layer that grows past the frame costs WebKit a
     backing store the size of its projection, not of what is visible: at 80 u the banner
     was 12,000 px wide and the portal frames 7,000, and iOS killed the page for the memory
     on the walk back up. At 3,000 px every one of these planes has already left the frame
     on a phone, so the cap changes nothing a visitor sees. */
  const CAP_PX = 3000;
  const GROW_END = 0.15;      // stage reaches the full viewport here
  const PANEL1_END = 0.22;
  const PANEL2_START = 0.55;
  const PANEL2_END = 0.9;
  const DARK_START = 0.85;
  /* The hand-off darkening never reaches solid. Once the pin releases, the stage scrolls
     away over its own height — a full viewport on a phone — and at opacity 1 that whole
     stretch was a dead black screen before the sponsors arrived. Holding a little of the
     hall visible keeps it reading as the end of the walk rather than as nothing, and the
     block below is the same ground colour, so the transition still closes cleanly. */
  const DARK_MAX = 0.88;

  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  /* Dynamic import through Function so a browser without import() support fails at
     call time (caught, DOM hero kept) instead of refusing to parse this whole file. */
  const dynImport = (url) => new Function('u', 'return import(u)')(url);

  /* Memory and cores test shared by the desktop path and the phone opt-in. */
  const hallCapable = () => {
    const mem = navigator.deviceMemory;
    const cores = navigator.hardwareConcurrency;
    return (mem === undefined || mem >= HALL_MIN_MEMORY) && (cores === undefined || cores >= HALL_MIN_CORES);
  };

  /* Live roots. A root whose DOM was replaced without shopify:section:unload (or by a
     script that swaps the section) still owns window listeners and a matchMedia
     context; sweep() destroys any that left the document. Runs on every initAll and
     from a dead root's own resize handler, so nothing leaks past the next init or
     resize. */
  const LIVE = new Set();
  const sweep = () => {
    LIVE.forEach((r) => { if (!r.isConnected && r.__dcHero) r.__dcHero.destroy(); });
  };

  const measureHeader = () => {
    let h = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
      h += el.getBoundingClientRect().height;
    });
    if (!h) {
      const hd = document.querySelector('#shopify-section-header, .section-header');
      if (hd) h = hd.getBoundingClientRect().height;
    }
    return Math.round(h);
  };

  const killRootTriggers = (root) => {
    const ev = window.__dcEvent;
    if (!ev) return;
    ev.ScrollTrigger.getAll().forEach((t) => {
      const el = t.trigger;
      const mine = t.vars.id && String(t.vars.id).indexOf('dc-hero:') === 0;
      if ((mine && (!el || root.contains(el))) || (el && !document.contains(el))) t.kill(true);
    });
  };

  const init = (root) => {
    if (root.__dcHero) { root.__dcHero.recompute(); return; }
    const stage = root.querySelector('[data-dc-stage]');
    const world = root.querySelector('[data-dc-world]');
    const scene = root.querySelector('[data-dc-scene]');
    if (!stage || !world || !scene) return;
    killRootTriggers(root);
    LIVE.add(root);

    const state = { H: 0, H0: 0, VH: window.innerHeight, s: 1, P: 1000, camZ0: 1500, camZ: 1500, n: 0, grow: 0, k: 1, horizon: 0.59, armed: false, motion: null, beatStarted: false };
    /* near: a plane's own hide distance (data-near), for things that leave the frame long
       before they reach the eye band. dim: its larger side in u, for the projected-size cap. */
    const planes = Array.from(root.querySelectorAll('.dc-hero__plane')).map((el) => ({
      el, z: parseFloat(el.style.getPropertyValue('--z')) || 0, op: 1, hidden: false,
      near: parseFloat(el.dataset.near) || 0,
      dim: Math.max(parseFloat(el.style.getPropertyValue('--w')) || 0, parseFloat(el.style.getPropertyValue('--h')) || 0),
    }));
    const els = {
      lamps: Array.from(root.querySelectorAll('.dc-hero__lamp')),
      cones: root.querySelector('.dc-hero__cones'),
      glow: root.querySelector('.dc-hero__floor-glow'),
      bannerArt: root.querySelector('.dc-hero__banner img'),
      pillarCopy: Array.from(root.querySelectorAll('.dc-hero__pillar-copy')),
      fixtures: Array.from(root.querySelectorAll('.dc-hero__fixture')),
      panel1: root.querySelector('[data-dc-panel="1"]'),
      panel2: root.querySelector('[data-dc-panel="2"]'),
      hint: root.querySelector('[data-dc-scrollhint]'),
      index: root.querySelector('.dc-hero__index'),
      dark: root.querySelector('[data-dc-dark]'),
      hallWrap: root.querySelector('[data-dc-hall]'),
      hallBtn: root.querySelector('[data-dc-hall-btn]'),
    };
    let setZ = null;
    let mm = null;
    let beat = null;
    /* Torii gate (JJ 2026-09-10): dc-gate.js draws the reference page's red gate on a
       transparent canvas over the world, its three.js camera driven from the same
       numbers as the CSS camera (gateSync). three.js (167 KB gz) and the engine load
       after init, so the landing paints first; until the gate is ready the entrance
       simply has no banner, and if it never comes (no WebGL, timeout) the flat banner
       and lamps return by dropping is-torii. */
    /* Entrance kinds: 'truss3d' (JJ 2026-09-11, the client's box-truss gate, the default),
       'torii' (the red gate), 'truss' (the flat banner under lamps, also what the WebGL
       kinds fall back to). Both WebGL kinds go through dc-gate.js; `kind` picks the builder. */
    const entrance = root.dataset.entrance || 'truss3d';
    const gateKind = entrance === 'torii' ? 'torii' : 'truss3d';
    const gate = {
      on: entrance !== 'truss' && !!root.querySelector('[data-dc-gate]'),
      kind: gateKind,
      canvas: root.querySelector('[data-dc-gate]'),
      handle: null, state: 'idle', raf: 0, last: 0, timer: 0, visible: true, reason: '',
      view: { W: 0, H: 0 },
    };
    const GATE_TIMEOUT = 12000;
    const loadScript = (src) => new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-dc-src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === '1') return resolve();
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('script failed ' + src)), { once: true });
        return undefined;
      }
      const s = document.createElement('script');
      s.src = src; s.async = true; s.dataset.dcSrc = src;
      s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve(); }, { once: true });
      s.addEventListener('error', () => reject(new Error('script failed ' + src)), { once: true });
      document.head.appendChild(s);
      return undefined;
    });
    const gateFail = (why) => {
      gate.state = 'failed';
      gate.reason = why;
      clearTimeout(gate.timer);
      root.classList.remove('is-torii', 'is-truss3d', 'is-gate');
      if (gate.timerId) { clearInterval(gate.timerId); gate.timerId = 0; }
      if (gate.handle) { try { gate.handle.destroy(); } catch (e) {} gate.handle = null; }
      try { console.debug('[dc-hero] ' + gateKind + ' gate skipped, flat banner kept: ' + why); } catch (e) {}
    };
    /* The LED timing panel on the truss (JJ 2026-09-11): the same instant as the pit board,
       2026-11-21T09:00:00+10:00, hard coded (the store timezone is wrong). Before the day the
       exact days : hours : minutes : seconds; on the day the phase lines. Computed once a
       second while the hero is on screen; the engine only redraws when the text changes. */
    const TIMER_T = 1795215600000;
    const timerSpec = (now) => {
      const left = TIMER_T - now;
      if (left > 0) {
        const s = Math.floor(left / 1000);
        const p = (n) => (n < 10 ? '0' : '') + n;
        return { lead: 'DOORS OPEN IN', groups: [
          { v: String(Math.floor(s / 86400)), l: 'DAYS' }, { v: p(Math.floor((s % 86400) / 3600)), l: 'HRS' },
          { v: p(Math.floor((s % 3600) / 60)), l: 'MIN' }, { v: p(s % 60), l: 'SEC' } ] };
      }
      const since = -left;
      if (since < 3600e3) return 'DOORS OPEN   EARLY ACCESS 9AM';
      if (since < 8 * 3600e3) return 'ON NOW   10AM TO 5PM';
      return 'THANK YOU EAGLEBY';
    };
    const timerTick = () => {
      if (!gate.handle || gate.state !== 'ready' || typeof gate.handle.setTimer !== 'function') return;
      if (!gate.visible && gate.handle.stats && gate.handle.stats.timer && gate.timerDrawn) return;
      const now = window.__dcPitboardNow ? window.__dcPitboardNow() : Date.now();
      gate.handle.setTimer(timerSpec(now));
      gate.timerDrawn = true;
    };
    const gateTier = () => ((navigator.maxTouchPoints || 0) > 0 || window.innerWidth <= 820 || (navigator.deviceMemory && navigator.deviceMemory < 4)) ? 'low' : 'high';
    const gateReduced = () => window.matchMedia(REDUCE).matches && !window.__dcForceMotion;
    const gateSync = () => {
      const h = gate.handle;
      if (!h) return;
      const W = gate.view.W || root.offsetWidth;
      const H = gate.view.H || state.H || root.offsetHeight;
      h.setView({ W, H, camX: 0, camY: 40, camZ: state.camZ, horizon: state.horizon });
    };
    const gateMeasure = () => {
      if (!gate.canvas) return;
      /* The world's own box, before its grow transform: width is the stage's, height is
         the design height applySize just wrote (the world is held there while pinned). */
      gate.view.W = root.offsetWidth;
      gate.view.H = state.H || world.offsetHeight;
      if (gate.handle) gate.handle.resize(gate.view.W, gate.view.H);
      gateSync();
      if (gateReduced() && gate.handle) gate.handle.render(0);
    };
    const gateFrame = (now) => {
      gate.raf = 0;
      if (!gate.handle || gate.state !== 'ready') return;
      const dt = gate.last ? Math.min((now - gate.last) / 1000, 0.05) : 0.016;
      gate.last = now;
      gateSync();
      gate.handle.render(dt);
      if (gate.visible && !gateReduced()) gate.raf = requestAnimationFrame(gateFrame);
    };
    const gateWake = () => { if (gate.state === 'ready' && !gate.raf) { gate.last = 0; gate.raf = requestAnimationFrame(gateFrame); } };
    const gateLoad = () => {
      if (!gate.on || gate.state !== 'idle') return;
      const canvas = gate.canvas;
      let gl = null;
      try { gl = canvas.getContext('webgl2') || canvas.getContext('webgl'); } catch (e) {}
      if (!gl) { gateFail('no WebGL'); return; }
      gate.state = 'loading';
      /* is-torii stands the flat banner and lamps down for either WebGL gate; is-truss3d
         hides what only the torii wanted (the DOM floor glow). */
      root.classList.add('is-torii');
      if (gateKind === 'truss3d') root.classList.add('is-truss3d');
      gate.timer = setTimeout(() => { if (gate.state === 'loading') gateFail('timeout after ' + GATE_TIMEOUT + ' ms'); }, GATE_TIMEOUT);
      const ds = root.dataset;
      loadScript(ds.gateThree).then(() => loadScript(ds.gateScript)).then(() => {
        if (gate.state !== 'loading') return undefined;
        if (!window.DCGate || typeof window.DCGate.mount !== 'function') throw new Error('dc-gate.js has no mount');
        const scale = parseFloat(ds.gateScale) || 82;
        const bw = parseFloat(ds.gateBannerW) || 580;
        /* Truss: a wider, taller black sheet laced inside the frame (the art letterboxed on
           the fill, the lamps' pools painted in), only a breath of motion. Torii: JJ
           2026-09-10, the black cloth with the artwork on the shimaki, not the foil. */
        const cloth = gateKind === 'truss3d'
          ? { width: Math.round(bw * 1.4), height: Math.round(bw * 1.4 / 3.0), aspect: 3.0, hang: 10, slits: false, look: 'cloth', glow: false, laced: 'sides', breath: 1.6, lightPools: true }
          : { width: bw, height: Math.round(bw * 0.3), hang: 10, slits: false, look: 'cloth', glow: false, hangFrom: 'shimaki' };
        gate.handle = window.DCGate.mount({
          canvas, tier: gateTier(), bannerUrl: ds.gateBanner, bannerFill: '#0b0b0d',
          gate: { scale, x: 0, y: -360, z: 0, ry: 0, kind: gateKind },
          cloth,
          /* The timing panel on the beam reads at 390 px only a size up (the phone camera sits
             at 2600 u, desktop 1500), so it grows there, takes the stacked face (lead over
             four full-width groups, no dot mask) and stays on the 11 m beam. */
          timer: window.matchMedia(MOBILE).matches ? { scale: 1.55, layout: 'stack' } : { scale: 1, layout: 'line' },
          hour: 18.5, motion: gateReduced() ? 0 : 1,
        });
        try { if (gate.handle.stats) console.debug('[dc-hero] ' + gateKind + ' gate: ' + gate.handle.stats.tris + ' tris, env ' + gate.handle.stats.env + ', tier ' + gate.handle.tier); } catch (e) {}
        return gate.handle.ready;
      }).then(() => {
        if (gate.state !== 'loading' || !gate.handle) return;
        clearTimeout(gate.timer);
        gate.state = 'ready';
        timerTick();
        if (!gate.timerId) gate.timerId = setInterval(timerTick, 1000);
        gateMeasure();
        gate.handle.render(0);
        root.classList.add('is-gate');
        gateWake();
      }).catch((e) => gateFail(String(e && e.message || e)));
    };
    /* Render only while the hero is on screen; iOS bills every live WebGL context. */
    if (gate.on && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        gate.visible = entries.some((en) => en.isIntersecting);
        if (gate.visible) gateWake();
      }, { rootMargin: '20% 0px' });
      io.observe(stage);
      gate.io = io;
    }
    let readyHandler = null;
    const beatWaiters = [];

    /* f, g, h. Spline hall state. state: idle (nothing applies yet), unavailable (gate
       failed, reason set), pending (desktop path waiting for the load-in beat), offered
       (phone button showing), loading, loaded, failed. */
    const hall = {
      state: 'idle', mode: null, reason: '', app: null, canvas: null, io: null,
      lastZ: null, raf: 0, timer: 0, token: 0, hasCam: false, loadedAt: 0,
      scene: (root.dataset.hallScene || '').trim(),
      runtime: (root.dataset.hallRuntime || '').trim(),
      desktop: root.dataset.hallDesktop === 'true',
      optIn: root.dataset.hallOptIn === 'true',
    };

    /* camZ goes to the scene once per animation frame. A scene without the camera
       variables (the stand-in) gets no calls at all: the runtime warns on every
       setVariable for a missing name, so hasCam (read from getVariables() at load)
       gates the push; hall.lastZ still tracks the scrub for QA. */
    const hallPush = () => {
      if (!hall.app || hall.state !== 'loaded') return;
      const z = Math.round(state.camZ * 100) / 100;
      if (z === hall.lastZ) return;
      hall.lastZ = z;
      if (!hall.hasCam) return;
      try { hall.app.setVariable('camZ', z); } catch (e) { /* runtime refused the value */ }
    };
    const hallTick = () => {
      if (!hall.app || hall.state !== 'loaded' || hall.raf) return;
      hall.raf = requestAnimationFrame(() => { hall.raf = 0; hallPush(); });
    };

    const setBtn = (mode) => {
      const b = els.hallBtn;
      if (!b) return;
      b.hidden = mode === 'hidden';
      b.disabled = mode === 'loading';
      b.dataset.hallMode = mode;
      b.setAttribute('aria-busy', mode === 'loading' ? 'true' : 'false');
      b.setAttribute('aria-pressed', mode === 'close' ? 'true' : 'false');
      const label = b.querySelector('[data-dc-hall-label]');
      if (label) label.textContent = mode === 'close' ? 'Close 3D' : mode === 'loading' ? 'Loading' : (b.dataset.label || 'Enter in 3D');
    };

    const hallTeardown = (next) => {
      hall.token++;
      clearTimeout(hall.timer);
      hall.timer = 0;
      if (hall.raf) { cancelAnimationFrame(hall.raf); hall.raf = 0; }
      if (hall.io) { try { hall.io.disconnect(); } catch (e) {} hall.io = null; }
      if (hall.app) { try { hall.app.dispose(); } catch (e) {} hall.app = null; }
      if (hall.canvas) { hall.canvas.remove(); hall.canvas = null; }
      root.classList.remove('is-hall', 'is-hall-loading');
      hall.lastZ = null;
      hall.hasCam = false;
      hall.mode = null;
      hall.loadedAt = 0;
      hall.state = next || 'idle';
    };

    const hallLoad = (mode) => {
      if (!els.hallWrap || !hall.scene || !hall.runtime) return;
      if (hall.state === 'loading' || hall.state === 'loaded') return;
      const token = ++hall.token;
      hall.state = 'loading';
      hall.mode = mode;
      hall.reason = '';
      root.classList.add('is-hall-loading');
      setBtn(mode === 'phone' ? 'loading' : 'hidden');
      const canvas = document.createElement('canvas');
      canvas.className = 'dc-hero__hall-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      els.hallWrap.appendChild(canvas);
      hall.canvas = canvas;
      let app = null;
      const fail = (why) => {
        if (token !== hall.token) return;
        if (!root.__dcHallLogged) {
          root.__dcHallLogged = true;
          try { console.debug('[dc-hero] Spline hall skipped, DOM hero kept: ' + why); } catch (e) {}
        }
        hallTeardown('failed');
        hall.reason = why;
        setBtn(mode === 'phone' ? 'offer' : 'hidden');
      };
      hall.timer = setTimeout(() => fail('timeout after ' + HALL_TIMEOUT + ' ms'), HALL_TIMEOUT);
      dynImport(hall.runtime).then((mod) => {
        if (token !== hall.token) return undefined;
        if (!mod || typeof mod.Application !== 'function') throw new Error('runtime has no Application export');
        app = new mod.Application(canvas);
        hall.app = app;
        return app.load(hall.scene);
      }).then(() => {
        if (token !== hall.token || !app) return;
        clearTimeout(hall.timer);
        hall.timer = 0;
        hall.state = 'loaded';
        hall.loadedAt = performance.now();
        let vars = {};
        try { vars = app.getVariables() || {}; } catch (e) {}
        const has = (k) => Object.prototype.hasOwnProperty.call(vars, k);
        hall.hasCam = has('camZ');
        try {
          if (has('camX')) app.setVariable('camX', 0);
          if (has('camY')) app.setVariable('camY', 40);
        } catch (e) {}
        hall.lastZ = null;
        hallPush();
        root.classList.remove('is-hall-loading');
        root.classList.add('is-hall');
        setBtn(mode === 'phone' ? 'close' : 'hidden');
        if ('IntersectionObserver' in window) {
          hall.io = new IntersectionObserver((entries) => {
            entries.forEach((en) => { try { en.isIntersecting ? app.play() : app.stop(); } catch (e) {} });
          }, { threshold: 0.01 });
          hall.io.observe(stage);
        }
      }).catch((e) => fail(e && e.message ? e.message : String(e)));
    };

    const hallClose = () => {
      const phone = hall.mode === 'phone';
      hallTeardown('idle');
      if (phone && hall.optIn && state.armed) { hall.state = 'offered'; setBtn('offer'); }
      else setBtn('hidden');
    };

    const whenBeat = (fn) => { if (state.beatStarted) fn(); else beatWaiters.push(fn); };
    const idle = (fn) => {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1000 });
      else setTimeout(fn, 200);
    };

    let hallEvalTimer = 0;
    const hallEvaluate = () => {
      hallEvalTimer = 0;
      if (hall.state === 'loading' || hall.state === 'loaded') return;
      if (!els.hallWrap || !hall.scene || !hall.runtime) {
        hall.state = 'unavailable'; hall.reason = 'no scene url'; setBtn('hidden'); return;
      }
      const ev = window.__dcEvent;
      const reduce = window.matchMedia(REDUCE).matches && !window.__dcForceMotion;
      const save = !!(ev && ev.saveData) || !!(navigator.connection && navigator.connection.saveData);
      if (reduce || save || !state.armed) {
        hall.state = 'unavailable';
        hall.reason = reduce ? 'reduced motion' : save ? 'save-data' : 'motion not armed';
        setBtn('hidden');
        return;
      }
      const desktopView = window.matchMedia(HALL_DESKTOP).matches && window.matchMedia(HALL_FINE).matches;
      const capable = hallCapable();
      if (desktopView) {
        if (hall.desktop && capable) {
          hall.state = 'pending';
          hall.reason = '';
          setBtn('hidden');
          whenBeat(() => idle(() => { if (state.armed && hall.state === 'pending') hallLoad('desktop'); }));
        } else {
          hall.state = 'unavailable';
          hall.reason = hall.desktop ? 'device under the memory or cores gate' : 'hall_on_desktop off';
          setBtn('hidden');
        }
        return;
      }
      if (hall.optIn && capable) {
        hall.state = 'offered';
        hall.reason = '';
        setBtn('offer');
      } else {
        hall.state = 'unavailable';
        hall.reason = hall.optIn ? 'device under the memory or cores gate' : 'hall_opt_in_mobile off';
        setBtn('hidden');
      }
    };
    const scheduleHallEvaluate = () => {
      if (hallEvalTimer) return;
      hallEvalTimer = setTimeout(hallEvaluate, 0);
    };

    const onHallBtn = () => {
      if (hall.state === 'loading' || hall.state === 'loaded') hallClose();
      else if (hall.state === 'offered' || hall.state === 'failed') hallLoad('phone');
    };
    if (els.hallBtn) els.hallBtn.addEventListener('click', onHallBtn);

    const hallMqs = [window.matchMedia(HALL_DESKTOP), window.matchMedia(HALL_FINE)];
    const onHallMq = () => {
      if (hall.state !== 'idle' && hall.state !== 'unavailable') hallTeardown('idle');
      setBtn('hidden');
      scheduleHallEvaluate();
    };
    hallMqs.forEach((q) => { if (q.addEventListener) q.addEventListener('change', onHallMq); });

    const applyCam = () => {
      if (!setZ || !state.armed) return;
      setZ(state.P - state.camZ * state.s);
      hallTick();
      if (gateReduced()) gateSync();
    };

    /* Writing the world scale for a height we already know, WITHOUT touching the DOM to
       find it out. The stage grows over the first fifteen percent of the scrub, and the
       old path re-ran the full recompute on every one of those frames: it measured the
       header (a querySelectorAll plus a rect per header group) and read the world's rect
       twice, then wrote six properties — a read, write, read thrash every frame, which is
       what put the layout-shift and the frame spikes into the walk. Nothing it measured
       can change mid-grow: the header does not resize while you scroll through the hero,
       and the height is the number setGrow just wrote. */
    const applySize = (H) => {
      if (!H) return;
      state.H = H;
      if (state.grow === 0) state.H0 = H;
      state.s = H / 900;
      state.P = 1.2071 * H;
      root.style.setProperty('--dc-s', state.s.toFixed(5));
      root.style.setProperty('--dc-P', state.P.toFixed(2));
      gateMeasure();
    };

    /* The full pass. Measures, so it belongs to init, resize and refresh — never a frame. */
    const recompute = () => {
      /* The viewport height the grow targets is taken HERE, once per recompute, and never
         inside the scrub. Read every frame it was a forced layout per frame for the whole
         pinned hero, and on a phone it moved with the address bar mid-scroll, so the
         stage's grown height stepped with the browser chrome instead of the scroll. */
      state.VH = window.innerHeight;
      root.style.setProperty('--dc-header', measureHeader() + 'px');
      const hz = parseFloat(getComputedStyle(root).getPropertyValue('--dc-horizon'));
      state.horizon = hz > 0 ? hz / 100 : 0.59;
      /* While motion runs the world is held at the landing height (see setGrow), and the
         stage may be carrying its grow. The design height is the stage's own CSS height,
         so measure it with both inline heights lifted for the one read, then put them
         back. Recompute runs on init, resize and refresh, never per frame, so the extra
         layout here is paid a handful of times per visit. */
      let H = 0;
      if (state.armed) {
        const sh = stage.style.height, smh = stage.style.maxHeight;
        stage.style.height = ''; stage.style.maxHeight = ''; world.style.height = '';
        H = stage.getBoundingClientRect().height;
        stage.style.height = sh; stage.style.maxHeight = smh;
        if (!H) return;
        state.H0 = H;
      } else {
        const r = world.getBoundingClientRect();
        H = r.height || stage.getBoundingClientRect().height;
        if (!H) return;
      }
      applySize(H);
      if (state.armed) world.style.height = state.H0 + 'px';
      state.camZ0 = window.matchMedia(MOBILE).matches ? 2600 : 1500;
      root.style.setProperty('--dc-camz', String(state.camZ0));
      root.dataset.dcH = String(Math.round(H));
      root.dataset.dcW = String(Math.round(stage.getBoundingClientRect().width));
      root.classList.add('is-sized');
      if (state.armed) {
        state.camZ = state.camZ0 - (state.camZ0 - CAM_END) * state.n;
        applyCam();
      }
    };

    const setLive = (el, live) => {
      if (!el) return;
      el.classList.toggle('is-live', live);
      if (el === els.panel1 || el === els.panel2) el.setAttribute('aria-hidden', live ? 'false' : 'true');
    };

    const markBeatStarted = () => {
      if (state.beatStarted) return;
      state.beatStarted = true;
      const fns = beatWaiters.splice(0);
      fns.forEach((fn) => { try { fn(); } catch (e) {} });
    };

    /* a. load-in beat */
    const playBeat = (gsap) => {
      if (root.__dcBeatPlayed) { root.classList.remove('is-preload'); markBeatStarted(); return; }
      root.__dcBeatPlayed = true;
      const rows = [];
      for (let i = 0; i < els.fixtures.length; i += 2) rows.push(els.fixtures.slice(i, i + 2));
      if (els.lamps.length) gsap.set(els.lamps, { autoAlpha: 0 });
      if (els.cones) gsap.set(els.cones, { opacity: 0 });
      if (els.glow) gsap.set(els.glow, { autoAlpha: 0 });
      if (els.bannerArt) gsap.set(els.bannerArt, { clipPath: 'inset(0 100% 0 0)' });
      if (els.pillarCopy.length) gsap.set(els.pillarCopy, { autoAlpha: 0, yPercent: -50, y: 20 });
      if (els.fixtures.length) gsap.set(els.fixtures, { autoAlpha: 0 });
      root.classList.remove('is-preload');
      const tl = gsap.timeline({
        paused: true,
        defaults: { ease: 'power2.out' },
        onComplete: () => { if (els.bannerArt) gsap.set(els.bannerArt, { clearProps: 'clipPath' }); },
      });
      if (els.lamps.length) tl.to(els.lamps, { autoAlpha: 1, duration: 0.6, ease: 'power3.out' }, 0.2);
      if (els.cones) tl.to(els.cones, { opacity: 1, duration: 0.6 }, 0.2);
      if (els.glow) tl.to(els.glow, { autoAlpha: 1, duration: 0.8 }, 0.35);
      if (els.bannerArt) tl.to(els.bannerArt, { clipPath: 'inset(0 0% 0 0)', duration: 0.8, ease: 'power2.inOut' }, 0.4);
      if (els.pillarCopy.length) tl.to(els.pillarCopy, { autoAlpha: 1, y: 0, duration: 0.7 }, 0.9);
      rows.forEach((row, i) => tl.fromTo(row, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4, ease: 'power1.out' }, 1.0 + i * 0.06));
      beat = tl;
      let started = false;
      const start = () => {
        if (started || beat !== tl) return;
        started = true;
        if (state.n > 0.1) tl.progress(1);
        else tl.play(0);
        markBeatStarted();
      };
      const img = els.bannerArt;
      if (img && img.decode && !img.complete) {
        img.decode().then(start, start);
        setTimeout(start, 1500);
      } else if (img && img.decode) {
        img.decode().then(start, start);
        setTimeout(start, 300);
      } else {
        start();
      }
    };

    /* e. static landing (reduced motion, Save-Data, or no runtime) */
    const staticLanding = () => {
      state.armed = false;
      state.motion = null;
      state.grow = 0;
      state.n = 0;
      hallTeardown('idle');
      setBtn('hidden');
      stage.style.height = '';
      stage.style.maxHeight = '';
      world.style.height = '';
      world.style.transform = '';
      state.k = 1;
      root.classList.remove('is-preload', 'is-motion');
      root.style.removeProperty('--dc-extra');
      recompute();
    };

    /* b, c, d. the pinned scrub */
    const buildMotion = (gsap, ST, ev, phone) => {
      const len = phone
        ? (parseFloat(root.dataset.scrollLength) || 300)
        : (parseFloat(root.dataset.scrollLengthDesktop) || 260);
      state.grow = 0;
      stage.style.height = '';
      stage.style.maxHeight = '';
      world.style.height = '';
      world.style.transform = '';
      recompute();
      state.armed = true;
      world.style.height = state.H0 + 'px';
      root.classList.add('is-motion');
      try { root.dispatchEvent(new CustomEvent('dc:hero:armed', { bubbles: true })); } catch (e) {}
      root.style.setProperty('--dc-extra', Math.max(0, window.innerHeight - state.H0) + 'px');
      playBeat(gsap);

      const proxy = { n: 0 };
      let lastLanding = null;
      let lastFloor = null;
      let lastBtnLive = null;
      let lastDark = -1;
      let lastH = -1;

      const setGrow = (g) => {
        state.grow = g;
        const target = Math.max(state.H0, state.VH || window.innerHeight);
        const h = g <= 0 ? 0 : state.H0 + (target - state.H0) * g;
        if (Math.abs(h - lastH) < 0.5) return;
        lastH = h;
        /* ScrollTrigger pins with inline height AND max-height, so write both. */
        stage.style.height = h ? h + 'px' : '';
        stage.style.maxHeight = h ? h + 'px' : '';
        /* The world does NOT grow with the stage. It stays at the landing height and
           scale, and its picture is enlarged to the grown stage with one compositor
           transform about the horizon. The two are the same image: every plane size,
           every position, the camera's translateZ and the perspective all scale with the
           stage height, so growing the world is a uniform scale of what it renders, and
           the horizon (a percentage of the stage) slides down by its share of the growth.
           The old path rewrote --dc-s and --dc-P every frame of the grow, which relaid
           out and REPAINTED all 23 planes per frame: WebKit's GPU process went from 166 MB
           to 1.28 GB across one pass through the grow on an iPhone profile, and iOS
           killed the tab at exactly this point of the walk ("A problem repeatedly
           occurred", JJ, 2026-09-04). A transform on the world costs no layout and no
           repaint. */
        const grown = h || state.H0;
        const k = grown / state.H0;
        state.k = k;
        const dy = state.horizon * (grown - state.H0);
        world.style.transform = k > 1.0005 ? 'translateY(' + dy.toFixed(2) + 'px) scale(' + k.toFixed(5) + ')' : '';
        /* The gate canvas is the same picture as the world: same held height, same grow. */
        if (gate.canvas) {
          gate.canvas.style.height = world.style.height;
          gate.canvas.style.transform = world.style.transform;
        }
      };

      const render = (n) => {
        state.n = n;
        state.camZ = state.camZ0 - (state.camZ0 - CAM_END) * n;
        setGrow(n < GROW_END ? n / GROW_END : 1);
        applyCam();
        for (let i = 0; i < planes.length; i++) {
          const p = planes[i];
          const dist = state.camZ - p.z;
          /* state.k: the grow scales the whole picture, so the projection is that much bigger */
          const hide = Math.max(EYE_HIDE, p.near, p.dim * state.P * state.k / CAP_PX);
          const band = Math.max(EYE_BAND, hide * 0.25);
          const op = dist <= hide ? 0 : dist >= hide + band ? 1 : (dist - hide) / band;
          const hidden = op <= 0;
          if (hidden !== p.hidden) {
            p.hidden = hidden;
            p.el.style.visibility = hidden ? 'hidden' : '';
          }
          if (op !== p.op) {
            p.op = op;
            p.el.style.opacity = op >= 1 ? '' : op.toFixed(3);
          }
        }
        const landing = n < PANEL1_END;
        if (landing !== lastLanding) {
          lastLanding = landing;
          setLive(els.panel1, landing);
          setLive(els.hint, landing);
          setLive(els.index, landing);
        }
        const floor = n >= PANEL2_START && n < PANEL2_END;
        if (floor !== lastFloor) {
          lastFloor = floor;
          setLive(els.panel2, floor);
        }
        const btnLive = n < DARK_START;
        if (btnLive !== lastBtnLive) {
          lastBtnLive = btnLive;
          if (els.hallBtn) els.hallBtn.classList.toggle('is-live', btnLive);
        }
        const d = clamp01((n - DARK_START) / (1 - DARK_START)) * DARK_MAX;
        if (els.dark && Math.abs(d - lastDark) > 0.004) {
          lastDark = d;
          els.dark.style.opacity = d <= 0 ? '' : d.toFixed(3);
        }
      };

      const tween = gsap.to(proxy, {
        n: 1,
        ease: 'none',
        duration: 1,
        onUpdate: () => render(proxy.n),
        scrollTrigger: {
          id: 'dc-hero:' + root.id,
          trigger: stage,
          pin: stage,
          pinSpacing: true,
          start: 'top top',
          end: () => '+=' + Math.round(window.innerHeight * len / 100),
          scrub: 0.6,
          /* 0, not 1. anticipatePin engages the pin early in proportion to scroll
             velocity, which is worth it when ScrollTrigger only hears about scroll a
             frame late. Lenis updates it synchronously (dc-event.js wires
             lenis.on('scroll', ScrollTrigger.update)), so there is nothing to
             anticipate and the early engage just snapped the stage 26 px up the
             moment the pin took hold. Measured: 26 px pop at 1, 0 px at 0, with no
             pinned jitter either way. */
          anticipatePin: 0,
          invalidateOnRefresh: true,
          refreshPriority: 10,
          onRefreshInit: () => { stage.style.height = ''; stage.style.maxHeight = ''; lastH = -1; state.grow = 0; },
          onRefresh: () => {
            recompute();
            root.style.setProperty('--dc-extra', Math.max(0, window.innerHeight - state.H0) + 'px');
            render(proxy.n);
          },
        },
      });

      const setProgress = (n) => {
        const st = tween.scrollTrigger;
        if (!st) return;
        const y = st.start + (st.end - st.start) * clamp01(n);
        if (ev.lenis) ev.lenis.scrollTo(y, { immediate: true, force: true, lock: false });
        else window.scrollTo(0, y);
        ST.update();
      };

      render(0);
      state.motion = { setProgress, tween, len, phone };
      scheduleHallEvaluate();

      return () => {
        state.armed = false;
        state.motion = null;
        hallTeardown('idle');
        setBtn('hidden');
        if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
        tween.kill();
        stage.style.height = '';
        stage.style.maxHeight = '';
        world.style.height = '';
        world.style.transform = '';
        if (gate.canvas) { gate.canvas.style.height = ''; gate.canvas.style.transform = ''; }
        root.classList.remove('is-motion');
        root.style.removeProperty('--dc-extra');
        gsap.set(scene, { clearProps: 'transform' });
        planes.forEach((p) => { p.el.style.opacity = ''; p.el.style.visibility = ''; p.op = 1; p.hidden = false; });
        setLive(els.panel1, true);
        setLive(els.hint, true);
        setLive(els.index, true);
        setLive(els.panel2, false);
        if (els.hallBtn) els.hallBtn.classList.add('is-live');
        if (els.dark) els.dark.style.opacity = '';
        state.grow = 0;
        state.n = 0;
        recompute();
      };
    };

    const arm = () => {
      const ev = window.__dcEvent;
      if (mm || !ev) return;
      const gsap = ev.gsap;
      const ST = ev.ScrollTrigger;
      setZ = gsap.quickSetter(scene, 'z', 'px');
      mm = gsap.matchMedia();
      mm.add({ phone: MOBILE, desktop: DESKTOP, reduce: REDUCE }, (ctx) => {
        const c = ctx.conditions || {};
        if ((c.reduce && !window.__dcForceMotion) || ev.saveData) { staticLanding(); return undefined; }
        return buildMotion(gsap, ST, ev, !!c.phone);
      });
    };

    let timer = 0;
    let lastW = window.innerWidth;
    /* A height-only resize on a touch device is the address bar coming and going, not a
       new viewport; dc-event.js already ignores those for ScrollTrigger and this handler
       was firing a full recompute (two forced layouts and a camera write) on every one of
       them mid-scroll. Width changes and orientation changes still recompute. */
    const onResize = (e) => {
      if (!root.isConnected) { if (root.__dcHero) root.__dcHero.destroy(); return; }
      const w = window.innerWidth;
      const heightOnly = w === lastW && (e && e.type) !== 'orientationchange';
      lastW = w;
      if (heightOnly && (navigator.maxTouchPoints || 0) > 0) return;
      clearTimeout(timer);
      timer = setTimeout(recompute, 120);
    };
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize);
    const mq = window.matchMedia(MOBILE);
    const onMq = () => recompute();
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(recompute).catch(() => {});

    recompute();
    requestAnimationFrame(recompute);
    if (gate.on) setTimeout(gateLoad, 300);

    root.__dcHero = {
      recompute,
      get gate() { return { state: gate.state, kind: gate.kind, reason: gate.reason, tier: gate.handle ? gateTier() : null, view: gate.view, handle: gate.handle }; },
      gateLoad,
      setProgress: (n) => { if (state.motion) state.motion.setProgress(n); },
      getCamZ: () => state.camZ,
      get progress() { return state.n; },
      get armed() { return state.armed; },
      get motion() { return state.motion; },
      get app() { return hall.app; },
      get hallState() { return hall.state; },
      get hall() {
        return {
          state: hall.state, mode: hall.mode, reason: hall.reason, hasCam: hall.hasCam,
          lastZ: hall.lastZ, loadedAt: hall.loadedAt, scene: hall.scene, runtime: hall.runtime,
          desktop: hall.desktop, optIn: hall.optIn, capable: hallCapable(),
          canvas: hall.canvas,
        };
      },
      hallLoad: (mode) => hallLoad(mode || (window.matchMedia(HALL_DESKTOP).matches ? 'desktop' : 'phone')),
      hallClose,
      /* QA only: pretend the loaded scene has the camera variables (or not) so the
         setVariable path can be exercised on a stand-in scene. */
      hallAssumeVars: (flag) => { hall.hasCam = !!flag; hall.lastZ = null; hallPush(); },
      get triggers() {
        const ev = window.__dcEvent;
        if (!ev) return [];
        return ev.ScrollTrigger.getAll().filter((t) => t.vars.id === 'dc-hero:' + root.id && t.trigger && root.contains(t.trigger));
      },
      destroy() {
        clearTimeout(timer);
        clearTimeout(hallEvalTimer);
        clearTimeout(gate.timer);
        if (gate.timerId) { clearInterval(gate.timerId); gate.timerId = 0; }
        if (gate.raf) cancelAnimationFrame(gate.raf);
        if (gate.io) gate.io.disconnect();
        if (gate.handle) { try { gate.handle.destroy(); } catch (e) {} gate.handle = null; }
        gate.state = 'idle';
        root.classList.remove('is-torii', 'is-truss3d', 'is-gate');
        hallEvalTimer = 0;
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
        if (mq.removeEventListener) mq.removeEventListener('change', onMq);
        hallMqs.forEach((q) => { if (q.removeEventListener) q.removeEventListener('change', onHallMq); });
        if (els.hallBtn) els.hallBtn.removeEventListener('click', onHallBtn);
        if (readyHandler) document.removeEventListener('dc:event:ready', readyHandler);
        if (mm) { mm.revert(); mm = null; }
        if (beat) { beat.kill(); beat = null; }
        beatWaiters.length = 0;
        killRootTriggers(root);
        staticLanding();
        root.__dcHero = null;
        LIVE.delete(root);
      },
    };

    if (window.__dcEvent) {
      arm();
    } else {
      readyHandler = () => { readyHandler = null; if (root.__dcHero) arm(); };
      document.addEventListener('dc:event:ready', readyHandler, { once: true });
    }
  };

  const initAll = (scope) => {
    sweep();
    (scope || document).querySelectorAll('[data-dc-hero]').forEach(init);
  };
  const recomputeAll = () => document.querySelectorAll('[data-dc-hero]').forEach((r) => r.__dcHero && r.__dcHero.recompute());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  window.addEventListener('load', recomputeAll);
  /* QA and lane C hook: window.__dcHero.init(root) re-inits one hero the way the
     theme editor's shopify:section:load does. */
  window.__dcHero = { init, initAll, recomputeAll };

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      initAll(e.target);
      if (window.__dcEvent) window.__dcEvent.refresh();
    });
    document.addEventListener('shopify:section:unload', (e) => {
      e.target.querySelectorAll('[data-dc-hero]').forEach((r) => r.__dcHero && r.__dcHero.destroy());
    });
  }

  /* Diagnostics panel: add ?dc-debug=1 (or #dc-debug) to the URL. Read-only, no effect on
     the hero; polls the public API twice a second. */
  (function () {
    if (!/dc-debug/.test(location.search + ' ' + location.hash)) return;
    const box = document.createElement('pre');
    box.id = 'dc-debug';
    box.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;max-width:min(92vw,560px);max-height:60vh;overflow:auto;margin:0;padding:10px 12px;background:rgba(0,0,0,.88);color:#9f9;font:11px/1.45 ui-monospace,Menlo,monospace;border:1px solid #3c3;border-radius:6px;white-space:pre-wrap;pointer-events:auto';
    const ua = navigator.userAgent;
    const engine = /Firefox\//.test(ua) ? 'Gecko (Firefox)' : /Edg\//.test(ua) ? 'Blink (Edge)' : /OPR\//.test(ua) ? 'Blink (Opera)' : /Chrome\//.test(ua) ? 'Blink (Chrome family)' : /Safari\//.test(ua) ? 'WebKit (Safari)' : 'unknown';
    const tick = () => {
      const h = document.querySelector('.dc-hero');
      const a = h && h.__dcHero;
      const ev = window.__dcEvent;
      const lines = [
        'DC HERO DEBUG  ' + new Date().toISOString().slice(11, 19),
        'engine: ' + engine,
        'ua: ' + ua.slice(0, 110),
        'viewport: ' + innerWidth + 'x' + innerHeight + '  dpr ' + devicePixelRatio + '  touch ' + (navigator.maxTouchPoints || 0),
        'prefers-reduced-motion: ' + matchMedia('(prefers-reduced-motion: reduce)').matches + '   force: ' + !!window.__dcForceMotion + '   save-data: ' + !!(navigator.connection && navigator.connection.saveData),
        'libs: gsap ' + typeof window.gsap + '  ScrollTrigger ' + typeof window.ScrollTrigger + '  Lenis ' + typeof window.Lenis + '  dcEvent ' + !!ev + '  lenis ' + !!(ev && ev.lenis),
        'hero: ' + (h ? h.className : 'NOT IN DOM') + '   api ' + !!a,
        'armed: ' + (a ? a.armed : '-') + '   progress ' + (a && a.progress != null ? Number(a.progress).toFixed(3) : '-') + '   camZ ' + (a && a.getCamZ ? Math.round(a.getCamZ()) : '-') + '   scrollY ' + Math.round(scrollY),
        'hall: ' + (a ? a.hallState + (a.hall && a.hall.reason ? ' (' + a.hall.reason + ')' : '') : '-') + '   pinSpacer ' + !!document.querySelector('.pin-spacer'),
        'errors: ' + ((window.__dcErrors || []).length ? '\n  ' + window.__dcErrors.slice(-6).join('\n  ') : 'none'),
      ];
      box.textContent = lines.join('\n');
    };
    const mount = () => { document.body.appendChild(box); tick(); setInterval(tick, 500); };
    if (document.body) mount(); else document.addEventListener('DOMContentLoaded', mount);
  })();
})();
